<template>
  <div>
    <!-- Top navbar -->
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow border-bottom" id="navbar-main">
      <div class="container-fluid">
        <router-link :to="{ name: 'laravelLogs' }" class="h4 mb-0 text-uppercase d-none d-lg-inline-block">
          Laravel Logs
        </router-link>
        <user-data></user-data>
      </div>
    </nav>

    <!-- Page content -->
    <div class="container-fluid mt-0 pt-5 pt-md-8 pb-5">

      <!-- Filters -->
      <div class="row mb-4">
        <div class="col-md-3">
          <b-form-group label="Date" label-for="logDate">
            <date-picker v-model="filters.date" :config="dateConfig" placeholder="All dates"
              class="form-control-alternative" @input="applyFilters"></date-picker>
          </b-form-group>
        </div>
        <div class="col-md-9">
          <b-form-group label="Level">
            <div class="d-flex flex-wrap mt-1" style="gap: 6px;">
              <b-btn size="sm" @click="setLevel('')"
                :variant="filters.level === '' ? 'secondary' : 'outline-secondary'">All</b-btn>
              <b-btn size="sm" v-for="lvl in levels" :key="lvl.value" @click="setLevel(lvl.value)"
                :variant="filters.level === lvl.value ? lvl.variant : ('outline-' + lvl.variant)">
                {{ lvl.label }}
              </b-btn>
            </div>
          </b-form-group>
        </div>
      </div>

      <!-- Log table -->
      <div class="row">
        <div class="col-md-12">
          <div class="card shadow">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">
                    Log Entries
                    <small class="text-muted ml-2" v-if="logData.total">({{ logData.total }} total)</small>
                  </h3>
                </div>
                <div class="col text-right text-muted small">
                  <span v-if="filters.date">{{ filters.date }}</span>
                  <span v-else>All dates</span>
                  <span class="ml-2">· {{ logData.source || 'laravel.log' }}</span>
                </div>
              </div>
            </div>

            <div v-if="isLoading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading logs..."></b-spinner>
            </div>

            <div class="table-responsive" v-else-if="logData.data && logData.data.length">
              <b-table class="mt-3 mb-0" striped hover :items="logData.data" :fields="fields">
                <template slot="timestamp" slot-scope="data">
                  <span class="text-nowrap small text-muted">{{ data.value }}</span>
                </template>
                <template slot="level" slot-scope="data">
                  <b-badge :variant="levelVariant(data.value)" class="text-uppercase px-2">{{ data.value }}</b-badge>
                </template>
                <template slot="message" slot-scope="data">
                  <span class="small">{{ truncate(data.value) }}</span>
                  <b-link v-if="data.value && data.value.length > 120" @click="showDetail(data.item)"
                    class="ml-1 text-muted" title="View full message">
                    <i class="fas fa-expand-alt fa-xs"></i>
                  </b-link>
                </template>
                <template slot="actions" slot-scope="data">
                  <b-link @click="showDetail(data.item)" class="text-muted" title="View full entry">
                    <i class="fas fa-search-plus"></i>
                  </b-link>
                </template>
              </b-table>
              <div class="card-footer border-0">
                <b-pagination align="right" @input="pageClick" :total-rows="logData.total"
                  v-model="currentPage" :per-page="logData.per_page || 50">
                </b-pagination>
              </div>
            </div>

            <div v-else class="text-center py-5 text-muted">
              <i class="fas fa-check-circle fa-2x mb-3 d-block text-success"></i>
              No log entries found
              <span v-if="filters.level"> for level <strong>{{ filters.level }}</strong></span>
              <span v-if="filters.date"> on <strong>{{ filters.date }}</strong></span>.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <b-modal ref="detailModal" title="Log Entry" ok-only ok-variant="secondary" ok-title="Close" centered size="lg">
      <div v-if="selectedEntry">
        <div class="d-flex align-items-center mb-3">
          <b-badge :variant="levelVariant(selectedEntry.level)" class="text-uppercase mr-2">{{ selectedEntry.level }}</b-badge>
          <small class="text-muted">{{ selectedEntry.timestamp }}</small>
          <small class="text-muted ml-auto">{{ selectedEntry.channel }} · {{ selectedEntry.source }}</small>
        </div>
        <pre class="small mb-0 p-3 bg-light rounded border"
          style="white-space: pre-wrap; word-break: break-word; max-height: 480px; overflow-y: auto;">{{ selectedEntry.message }}</pre>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import datePicker from 'vue-bootstrap-datetimepicker'
  import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'
  import userData from '@/pages/Common/userData'

  export default {
    components: { userData, datePicker },
    data () {
      return {
        isLoading: false,
        currentPage: 1,
        selectedEntry: null,
        filters: {
          date: '',
          level: ''
        },
        dateConfig: {
          format: 'YYYY-MM-DD',
          useCurrent: false,
        },
        levels: [
          { value: 'debug',     label: 'Debug',     variant: 'secondary' },
          { value: 'info',      label: 'Info',      variant: 'info' },
          { value: 'notice',    label: 'Notice',    variant: 'primary' },
          { value: 'warning',   label: 'Warning',   variant: 'warning' },
          { value: 'error',     label: 'Error',     variant: 'danger' },
          { value: 'critical',  label: 'Critical',  variant: 'danger' },
          { value: 'alert',     label: 'Alert',     variant: 'danger' },
          { value: 'emergency', label: 'Emergency', variant: 'dark' },
        ],
        fields: [
          { key: 'timestamp', label: 'Timestamp', sortable: false, class: 'text-nowrap align-middle' },
          { key: 'level',     label: 'Level',     sortable: false, class: 'text-center align-middle' },
          { key: 'message',   label: 'Message',   sortable: false, class: 'align-middle' },
          { key: 'actions',   label: '',          sortable: false, class: 'text-center align-middle' },
        ]
      }
    },
    computed: {
      ...mapGetters('logs', ['logData'])
    },
    methods: {
      ...mapActions('logs', ['FETCH_LOGS']),
      fetchLogs () {
        this.isLoading = true
        const params = { page: this.currentPage }
        if (this.filters.date)  params.date  = this.filters.date
        if (this.filters.level) params.level = this.filters.level
        this.FETCH_LOGS(params).finally(() => {
          this.isLoading = false
        })
      },
      applyFilters () {
        this.currentPage = 1
        this.fetchLogs()
      },
      setLevel (level) {
        this.filters.level = level
        this.applyFilters()
      },
      pageClick (page) {
        this.currentPage = page
        this.fetchLogs()
      },
      levelVariant (level) {
        return {
          debug: 'secondary', info: 'info', notice: 'primary',
          warning: 'warning', error: 'danger', critical: 'danger',
          alert: 'danger', emergency: 'dark'
        }[level] || 'secondary'
      },
      truncate (str, len = 120) {
        if (!str) return ''
        return str.length > len ? str.slice(0, len) + '…' : str
      },
      showDetail (entry) {
        this.selectedEntry = entry
        this.$refs.detailModal.show()
      }
    },
    created () {
      this.fetchLogs()
    }
  }
</script>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/laravelLogs.vue